import MemberPopoverContent from "@components/MemberPopoverContent";
import { IconLoading, IconLock } from "@icons/index";
import { Popover } from "antd";
import CommonButton from "@components/CommonButton";
import { Trans } from "react-i18next";
import report from "@report";
import { useOpenWindow } from "@utils/window";
import { PAGES } from "@routes";
import { useCallback } from "react";
import { useVideoScript } from "./hooks";
import { useMembershipLevel } from "@hooks/membership";
import { useAppSelector } from "@hooks/redux";
import { IconLineAI, IconLineScript } from "@kalodata/kds-design-icons";

interface ScriptBtnGroupProps {
  kalo_pos?: string;
  videoId: string;
  videoSource: string;
  onBtnClick?: () => void;
}
const ScriptBtnGroup = ({ kalo_pos, videoId, videoSource, onBtnClick }: ScriptBtnGroupProps) => {
  const { openWindow } = useOpenWindow();
  const { isBasicUp } = useMembershipLevel();
  const { hasPurchased, status, checkAiUse } = useVideoScript();
  const { totalRemain } = useAppSelector((state) => state.globalReducer.creditInfo);

  const renderBtnMask = useCallback(
    (remainCreditLimit: number = 0) => {
      if (status === "loading") {
        return (
          <div className="absolute z-10 top-0 left-0 w-full h-full flex items-center justify-center opacity-100 transition-opacity">
            <IconLoading className="animate-spin icon" />
          </div>
        );
      }

      if (!isBasicUp) {
        return (
          <Popover
            content={<MemberPopoverContent desc="Upgrade to use the feature" action="Upgrade"></MemberPopoverContent>}
            trigger="hover"
          >
            <div className="absolute z-10 top-0 left-0 w-full h-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
              <IconLock className="absolute opacity-100"></IconLock>
            </div>
          </Popover>
        );
      }

      if (totalRemain < remainCreditLimit || totalRemain === 0) {
        return (
          <Popover
            content={<MemberPopoverContent remainCredit={totalRemain} action="Credits"></MemberPopoverContent>}
            trigger="hover"
          >
            <div className="absolute z-10 top-0 left-0 w-full h-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
              <IconLock className="absolute opacity-100"></IconLock>
            </div>
          </Popover>
        );
      }
    },
    [status, isBasicUp, totalRemain],
  );

  return (
    <div className="relative flex gap-[8px] w-full h-[50px]">
      <div className="relative flex-1">
        {renderBtnMask(1)}
        <CommonButton
          size="large"
          disabled={(!hasPurchased && totalRemain < 1) || !isBasicUp || status === "loading"}
          icon={<IconLineScript className="relative" size={20} />}
          className="w-full h-full"
          onClick={() => {
            report.click({
              kalo_type: "script_optimizer",
              kalo_pos,
            });
            onBtnClick && onBtnClick();
            openWindow(PAGES.AI_VIDEO_INSIGHT + `?id=${videoId}&videoSource=${videoSource}`);
          }}
          onMouseEnter={() => {
            checkAiUse(videoId);
          }}
        >
          <span className="text-[12px]">
            <Trans>{"View Storyboard"}</Trans>
          </span>
        </CommonButton>
      </div>
      <div className="relative flex-1">
        {renderBtnMask(0)}
        <CommonButton
          type="primary"
          size="large"
          autoPath={false}
          disabled={totalRemain === 0 || !isBasicUp}
          className="w-full h-full"
          icon={<IconLineAI className="relative" size={20} />}
          onClick={() => {
            report.click({
              kalo_type: "script_optimizer",
              kalo_pos,
            });
            onBtnClick && onBtnClick();
            openWindow(PAGES.AI_SCRIPT_REWRITE + `?id=${videoId}&videoSource=${videoSource}`);
          }}
        >
          <span className="text-[12px]">
            <Trans>AI Script Writer</Trans>
          </span>
        </CommonButton>
      </div>
    </div>
  );
};

export default ScriptBtnGroup;
